import { add, format } from 'date-fns';
import { FunctionComponent } from 'react';
import { FormattedDate } from 'react-intl';

import { STANDARD_DATE_FORMAT_DATE_FNS } from '@savgroup-front-common/constants';
import { Timeslot } from '@savgroup-front-common/types';

import { Tag } from '../../../atoms/Tag';
import { TAG_VARIANT } from '../../../atoms/Tag/Tag.types';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import { useGetPageSize } from '../../InterventionAppointment/hooks/useGetPageSize';
import { byStartLocalTime } from '../AppointmentCalendar.helpers';
import {
  CalendarDayBlock,
  CalendarDayDate,
  CalendarDayName,
  TimeSlotSelectorContainer,
} from '../AppointmentCalendar.styles';
import messages from '../messages';
import { DetailedSchedule } from '../types/DetailedSchedule';

import {
  $AppointmentCalendarScreen,
  $EmptyDay,
  $TagContainer,
  $TimeSlotSelectorContainer,
  $UnderlinedRow,
} from './AppointmentCalendarScreen.styles';
import TimeSlotSelector from './TimeSlotSelector';

interface AppointmentCalendarScreenProps {
  isActive: boolean;
  selectedTimeslot?: Timeslot;
  schedules: DetailedSchedule[];
  onTimeslotSelect: ({
    selectedTimeslot,
  }: {
    selectedTimeslot?: Timeslot;
  }) => void;
  startTime?: Date;
}

const AppointmentCalendarScreen: FunctionComponent<
  AppointmentCalendarScreenProps
> = ({
  isActive,
  selectedTimeslot,
  schedules,
  onTimeslotSelect,
  startTime,
}) => {
  const pageSize = useGetPageSize();

  const isAllDetailedScheduleEmpty = (schedules || []).every(
    ({ timeslots = [] }) =>
      timeslots.filter((timeslot) => !!timeslot).length === 0,
  );

  const firstDate = startTime || schedules[0]?.date;

  const dateList: Date[] = [];

  if (firstDate) {
    for (let i = 0; i < pageSize; i += 1) {
      dateList.push(add(firstDate, { days: i }));
    }
  }

  return (
    <$AppointmentCalendarScreen>
      <$UnderlinedRow $columnCount={pageSize}>
        {dateList.map((date) => (
          <div key={date.toISOString()}>
            <CalendarDayBlock>
              <CalendarDayName>
                <FormattedDate value={date} weekday="long" />
              </CalendarDayName>
              <CalendarDayDate>
                <FormattedDate value={date} day="2-digit" month="2-digit" />
              </CalendarDayDate>
            </CalendarDayBlock>
          </div>
        ))}
      </$UnderlinedRow>
      {isAllDetailedScheduleEmpty && (
        <$TagContainer>
          <Tag variant={TAG_VARIANT.ALERT} noEllispis>
            <SafeFormattedMessageWithoutSpread
              message={messages.appointmentCalendarEmpty}
            />
          </Tag>
        </$TagContainer>
      )}
      {!isAllDetailedScheduleEmpty && (
        <$TimeSlotSelectorContainer $columnCount={pageSize}>
          {dateList.map((date) => {
            const detailedSchedule = schedules.find(
              (schedule) =>
                format(schedule.date, STANDARD_DATE_FORMAT_DATE_FNS) ===
                format(date, STANDARD_DATE_FORMAT_DATE_FNS),
            );

            const timeslots = detailedSchedule?.timeslots || [];

            if (timeslots?.length === 0) {
              return <$EmptyDay key={date.toISOString()}>-</$EmptyDay>;
            }

            const allDaySchedule = timeslots.find(
              (timeslot) => timeslot.allDaySchedule,
            );

            if (allDaySchedule) {
              return (
                <div key={date.toISOString()}>
                  <TimeSlotSelector
                    isActive={isActive}
                    timeslot={allDaySchedule}
                    onTimeslotSelect={onTimeslotSelect}
                    selectedTimeslot={selectedTimeslot}
                  />
                </div>
              );
            }

            return (
              <div key={date.toISOString()}>
                <TimeSlotSelectorContainer>
                  {timeslots.sort(byStartLocalTime).map((timeslot) => {
                    return (
                      <TimeSlotSelector
                        key={timeslot.internalId}
                        isActive={isActive}
                        timeslot={timeslot}
                        onTimeslotSelect={onTimeslotSelect}
                        selectedTimeslot={selectedTimeslot}
                      />
                    );
                  })}
                </TimeSlotSelectorContainer>
              </div>
            );
          })}
        </$TimeSlotSelectorContainer>
      )}
    </$AppointmentCalendarScreen>
  );
};

AppointmentCalendarScreen.displayName = 'AppointmentCalendarScreen';

export default AppointmentCalendarScreen;
